import { type ReactNode, useEffect } from 'react';
import { Provider } from 'react-redux';
import { Elements } from '@stripe/react-stripe-js';

import NotificationsContainer from '../components/order/ErrorNotifications/Notifications';
import HeadScripts from './parts/HeadScripts';
import StepsSwitch from '@/components/affiliate/StepsSwitch';
import ErrorBoundary from '@/components/error/ErrorBoundary';
import LocalizationProvider from '@/components/new-order/lang/LocalizationProvider';

import { fetchAPIData } from '@/store/affiliate/actions/fetchAPIData';
import { affilateStore, useAffiliateDispatch } from '@/store/affiliate';

import { stripePromise as stripe } from '@/utils/stripe/affiliateOrderSubmitAdapter';
import { rootElementOptions } from '@/utils/stripe/fieldComponentOptions';
import { setInstanceAppIdentity } from '@/utils/api/clearApiClient';
import AnalyticsEventHandler from '@/services/analytics/AnalyticsEventHandler';
import { type OneOfAppIdentities } from '@/utils/consts/appIdentity';
import { setIsWebRoute } from '@/store/affiliate/flagsSlice';
import { setExcludeWeeklyPlans } from '@/store/featureFlagsSlice';

const PageWrapper = ({ children }: { children: ReactNode }) => (
  <>
    <HeadScripts />
    <ErrorBoundary>
      <LocalizationProvider>
        <Provider store={affilateStore}>
          <Elements stripe={stripe} options={rootElementOptions}>
            {children}
          </Elements>
        </Provider>
      </LocalizationProvider>
    </ErrorBoundary>
  </>
);

interface AffiliateOrderProps {
  customAppIdentity: OneOfAppIdentities;
  isWebRoute?: boolean;
  excludeWeeklyPlans?: boolean;
}

// separating wrapper, so we can use dispatch here
const AffiliateOrderContent = ({ customAppIdentity, isWebRoute, excludeWeeklyPlans = false }: AffiliateOrderProps) => {
  const dispatch = useAffiliateDispatch();

  useEffect(() => {
    if (customAppIdentity) {
      setInstanceAppIdentity(customAppIdentity);
    }
    if (isWebRoute) {
      dispatch(setIsWebRoute(true));
    }

    dispatch(setExcludeWeeklyPlans(excludeWeeklyPlans));

    AnalyticsEventHandler.init();
    void dispatch(fetchAPIData());
  }, []);

  return (
    <PageWrapper>
      <StepsSwitch />

      <NotificationsContainer />
    </PageWrapper>
  );
};

const AffiliateOrder = (props: AffiliateOrderProps) => (
  <PageWrapper>
    <AffiliateOrderContent {...props} />
  </PageWrapper>
);

export default AffiliateOrder;
